<template>
  <div class="widget" no-body>
    <div class="widget-label">
      <div class="label">
        <p>{{ titleHeader }}</p>
      </div>
      <b-button
        v-if="allowAdd"
        @click="addProjectMetric"
        variant="primary"
        class="right-align"
      >
        <font-awesome-icon icon="plus" size="1x" />
      </b-button>
    </div>
    <b-list-group v-if="projectMetrics.length > 0" class="survey metric__list">
      <b-list-group-item
        v-for="item in displayableProjectMetrics"
        :key="item.projectMetricId"
        :class="borderClassByRole(item)"
        class="mr-0 metric__item"
        :button="!viewByLink"
      >
        <div>
          <div v-if="viewByLink">
            <b-badge v-if="showTypeBadge">{{ item.type }}</b-badge>
            <strong>{{ item.externalId }}</strong>
            <b-link @click="viewProjectMetric(item.projectMetricId)">
              {{ item.name }}
            </b-link>
          </div>
          <div v-else>
            <b-badge v-if="showTypeBadge">{{ item.type }}</b-badge>
            <strong>{{ item.externalId }}</strong>
            {{ item.name }}
          </div>
          <p
            v-if="showDueDate && item.dateDue"
            class="card-text small text-muted"
          >
            Due: {{ toDisplayDate(item.dateDue) }}
          </p>
        </div>
        <div v-if="action !== 'None'" cols="auto" align-self="end">
          <b-button
            v-if="action === 'View'"
            v-on:click="viewProjectMetric(item.projectMetricId)"
            class="float-right text-button"
          >
            View
          </b-button>
          <b-button
            size="sm"
            v-if="action === 'Edit'"
            v-on:click="editProjectMetric(item.projectMetricId)"
            class="float-right text-button"
          >
            <font-awesome-icon icon="edit" size="2x"></font-awesome-icon>
          </b-button>
          <b-button
            size="sm"
            v-if="action === 'Close'"
            v-on:click="closeProjectMetric(item.projectMetricId)"
            class="float-right text-button"
            style="background: transparent"
          >
            <font-awesome-icon
              :icon="['far', 'check-circle']"
              size="2x"
            ></font-awesome-icon>
          </b-button>
          <b-button
            v-if="action === 'Undo'"
            v-on:click="undoMarkClosed(item.projectMetricId)"
            class="float-right text-button"
          >
            <b-spinner
              v-if="sending"
              style="width: 1.4rem; height: 1.4rem"
            ></b-spinner>
            <font-awesome-icon
              v-else
              icon="undo-alt"
              size="lg"
            ></font-awesome-icon>
          </b-button>
        </div>
        <div
          v-if="!item.lessonLearnedId && action === 'LessonLearned'"
          cols="auto"
          lg="auto"
          align-self="end"
        >
          <b-button
            v-if="action === 'LessonLearned'"
            class="float-right"
            v-on:click="showMarkLessonLearnedModal(item.projectMetricId)"
          >
            Add as Lesson Learned
          </b-button>
          <b-modal
            :ref="'mark-lesson-learned-modal-' + item.projectMetricId"
            title="Add Lesson Learned"
            hide-footer
          >
            <b-row>
              <b-col>
                <b-form
                  v-if="showMarkLessonLearnedModal"
                  @submit.prevent="
                    onMarkLessonLearnedSubmit(item.projectMetricId)
                  "
                  @reset.prevent="
                    onMarkLessonLearnedCancel(item.projectMetricId)
                  "
                >
                  <b-form-group
                    label="Name"
                    label-for="mark-lesson-learned-name"
                    invalid-feedback="Please enter a name"
                  >
                    <b-form-input
                      id="new-lesson-learned-name"
                      v-model="markLessonLearnedForm.name"
                      required
                      maxlength="100"
                    ></b-form-input>
                  </b-form-group>
                  <b-form-group
                    label="Description"
                    label-for="mark-lesson-learned-description"
                    invalid-feedback="Please enter a description"
                  >
                    <b-form-textarea
                      id="mark-lesson-learned-description"
                      v-model="markLessonLearnedForm.description"
                      rows="5"
                      autofocus
                      maxlength="500"
                    ></b-form-textarea>
                  </b-form-group>
                  <b-button
                    type="submit"
                    variant="primary"
                    class="float-right ml-2"
                  >
                    Add
                  </b-button>
                  <b-button type="reset" class="float-right">Cancel</b-button>
                </b-form>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <BaseMessageBlock
                  :messages="markLessonLearnedErrors"
                ></BaseMessageBlock>
              </b-col>
            </b-row>
          </b-modal>
        </div>
      </b-list-group-item>
    </b-list-group>
    <b-card-footer
      v-if="showLimit !== 0 && projectMetrics.length > showLimit"
      class="no-border__footer"
      style="background: white"
    >
      <b-button class="text-button" @click="showModal('more-actions-modal')">
        Show all {{ projectMetrics.length }}
      </b-button>
      <b-modal ref="more-actions-modal" :title="title" ok-only ok-title="Close">
        <b-list-group v-if="projectMetrics.length > 0">
          <b-list-group-item
            v-for="item in projectMetrics"
            :key="item.aid"
            :class="borderClassByRole(item)"
          >
            <b-row>
              <b-col>
                <div v-if="viewByLink">
                  <b-badge v-if="showTypeBadge">{{ item.type }}</b-badge>
                  <strong>{{ item.externalId }}</strong>
                  <b-link @click="viewProjectMetric(item.projectMetricId)">
                    {{ item.name }}
                  </b-link>
                </div>
                <div v-else>
                  <b-badge v-if="showTypeBadge">{{ item.type }}</b-badge>
                  <strong>{{ item.externalId }}</strong>
                  {{ item.name }}
                </div>
                <p
                  v-if="showDueDate && item.dateDue"
                  class="card-text small text-muted"
                >
                  Due: {{ toDisplayDate(item.dateDue) }}
                </p>
              </b-col>
              <b-col cols="3" xl="3" align-self="end">
                <b-button
                  v-if="action === 'View'"
                  v-on:click="viewProjectMetric(item.projectMetricId)"
                  class="float-right"
                >
                  View
                </b-button>
                <b-button
                  size="sm"
                  v-if="action === 'Edit'"
                  v-on:click="editProjectMetric(item.projectMetricId)"
                  class="float-right text-button"
                >
                  <font-awesome-icon icon="edit" size="2x"></font-awesome-icon>
                </b-button>
                <b-button
                  size="sm"
                  v-if="action === 'Close'"
                  v-on:click="showMarkClosedModal(item.projectMetricId)"
                  class="float-right text-button"
                >
                  <font-awesome-icon
                    :icon="['far', 'check-circle']"
                    size="2x"
                  ></font-awesome-icon>
                </b-button>
                <b-button
                  v-if="action === 'Undo'"
                  v-on:click="undoMarkClosed(item.projectMetricId)"
                  class="float-right text-button"
                >
                  <b-spinner
                    v-if="sending"
                    style="width: 1.4rem; height: 1.4rem"
                  ></b-spinner>
                  <font-awesome-icon
                    v-else
                    icon="undo-alt"
                    size="lg"
                  ></font-awesome-icon>
                </b-button>
              </b-col>
            </b-row>
          </b-list-group-item>
        </b-list-group>
      </b-modal>
    </b-card-footer>
    <b-card-text v-if="!(projectMetrics.length > 0)" style="margin: 20px">
      <p>{{ noneText }}</p>
    </b-card-text>
    <EditProjectMetricModal
      ref="add-project-metric-modal"
      v-if="allowAdd"
      :projectId="projectId"
      :title="addTitle"
      :mode="'Add'"
      :possibleAssignees="possibleAssignees"
      :addTypes="addTypes"
      :presetTags="presetTags"
      :dataTemplate="dataTemplate"
      :meetingId="meetingId"
      @submit="addProjectMetricSubmit"
    >
    </EditProjectMetricModal>
    <EditProjectMetricModal
      :key="editProjectMetricKey"
      v-if="action === 'Edit'"
      ref="edit-project-metric-modal"
      :projectId="projectId"
      :projectMetric="editingProjectMetric"
      :possibleAssignees="possibleAssignees"
      :addTypes="addTypes"
      :presetTags="presetTags"
      :dataTemplate="
        dataTemplateByType(
          editingProjectMetric ? editingProjectMetric.type : null
        )
      "
      :meetingId="meetingId"
      @submit="editProjectMetricSubmit"
    >
    </EditProjectMetricModal>
    <CloseProjectMetricModal
      ref="close-project-metric-modal"
      :projectId="projectId"
      v-if="action === 'Close'"
      :title="'Close Action Item'"
      :mode="'Close'"
      :projectMetric="closingProjectMetric"
      :possibleAssignees="possibleAssignees"
      :addTypes="addTypes"
      :presetTags="presetTags"
      :meetingId="meetingId"
      @submit="closeProjectMetricSubmit"
    />
    <ViewProjectMetricModal
      ref="view-project-metric-modal"
      :projectMetric="viewingProjectMetric"
      @refresh="refreshProjectMetric"
    ></ViewProjectMetricModal>
  </div>
</template>

<script>
import axios from "axios";
import EditProjectMetricModal from "@/components/modals/EditProjectMetricModal.vue";
import ViewProjectMetricModal from "@/components/modals/ViewProjectMetricModal.vue";
import CloseProjectMetricModal from "@/components/modals/CloseProjectMetricModal.vue";

export default {
  components: {
    EditProjectMetricModal,
    ViewProjectMetricModal,
    CloseProjectMetricModal,
  },
  props: {
    projectMetrics: { type: Array, default: () => [] },
    projectId: String,
    title: { type: String, default: "Action Items" },
    addTitle: { type: String, default: "New Action Item" },
    noneText: { type: String, default: "No Action Items" },
    action: { type: String, default: "None" },
    viewByLink: { type: Boolean, default: true },
    allowAdd: { type: Boolean, default: false },
    addTypes: {
      type: Array,
      default: () => [
        "Risk",
        "Issue",
        "Efficiency",
        "Innovation",
        "Partnership",
        "Sustainability",
      ],
    },
    possibleAssignees: { type: Array, default: () => [] },
    showTypeBadge: { type: Boolean, default: false },
    showRemainingCount: { type: Boolean, default: false },
    showLimit: { type: Number, default: 5 },
    showDueDate: { type: Boolean, default: false },
    dataTemplate: Object,
    cardClass: { type: String, default: "mb-4" },
    presetTags: { type: Array, default: () => [] },
    meetingId: { type: String, default: "" },
  },
  data() {
    return {
      errors: [],
      markLessonLearnedErrors: [],
      showMarkLessonLearnedForm: true,
      editingProjectMetric: null,
      viewingProjectMetricId: "",
      closingProjectMetric: null,
      markLessonLearnedForm: {
        name: "",
        description: "",
      },
      sending: false,
      editProjectMetricKey: 0,
    };
  },
  methods: {
    clickHandle(item, action) {
      switch (action) {
        case "View":
          this.viewProjectMetric(item.projectMetricId);
          break;
        case "Edit":
          this.editProjectMetric(item.projectMetricId);
          break;
        case "Close":
          this.closeProjectMetric(item.projectMetricId);
          break;
        case "Undo":
          this.undoMarkClosed(item.projectMetricId);
          break;
        default:
          //console.error("Error with click handle", action, item);
          break;
      }
    },
    showModal(name) {
      this.$refs[name].show();
    },
    hideModal(name) {
      this.$refs[name].hide();
    },
    showModalVFor(name) {
      this.$refs[name][0].show();
    },
    hideModalVFor(name) {
      this.$refs[name][0].hide();
    },
    toDisplayDate(date) {
      return this.$helpers.displayDate(date);
    },
    undoMarkClosed(id) {
      let projectMetric = this.projectMetrics.find(
        (a) => a.projectMetricId === id
      );

      let request = {
        ...projectMetric,
        dateClosed: null,
        resolution: "",
        resolutionLevel: "",
        dateResolved: null,
        resolvedBy: "",
        timeSaved: null,
        costSaved: null,
        mitigationMethod: "",
        mitigationMethodData: "",
        status: "Open",
      };

      this.sending = true;

      let url = `${this.$api_url}/project-metrics/update`;
      if (this.meetingId) {
        url += `?meetingId=${this.meetingId}`;
      }

      axios
        .post(url, request, this.getReqConfig())
        .then(() => {
          this.$emit("update");
        })
        .catch((err) => {
          this.$emit("error", err);
        })
        .finally(() => {
          this.sending = false;
        });
    },
    //Mark LessonLearned
    validateMarkLessonLearned() {
      const initErrors = this.markLessonLearnedErrors.length;
      this.markLessonLearnedForm = this.$helpers.trimObject(
        this.markLessonLearnedForm
      );

      if (this.markLessonLearnedForm.name.length === 0) {
        this.markLessonLearnedErrors.push({ message: "Please enter Name" });
      }

      return initErrors === this.markLessonLearnedErrors.length;
    },
    showMarkLessonLearnedModal(projectMetricId) {
      let item = this.projectMetrics.find(
        (a) => a.projectMetricId === projectMetricId
      );
      this.markLessonLearnedForm.name = item.name;
      this.showModalVFor("mark-lesson-learned-modal-" + projectMetricId);
    },
    onMarkLessonLearnedSubmit(projectMetricId) {
      this.markLessonLearnedErrors = [];
      if (this.validateMarkLessonLearned()) {
        let item = this.projectMetrics.find(
          (a) => a.projectMetricId === projectMetricId
        );

        let lessonLearnedId = this.$helpers.generateUUID();
        let lessonLearned = {
          lessonLearnedId: lessonLearnedId,
          projectId: item.projectId,
          name: this.markLessonLearnedForm.name,
          description: this.markLessonLearnedForm.description,
          projectMetricId: projectMetricId,
        };
        item.lessonLearnedId = lessonLearnedId;

        this.$parent.handleAddLessonLearned(lessonLearned);
        this.resetMarkLessonLearnedForm();
        this.hideModalVFor("mark-lesson-learned-modal-" + projectMetricId);
      }
    },
    onMarkLessonLearnedCancel(projectMetricId) {
      this.resetMarkLessonLearnedForm();
      this.hideModalVFor("mark-lesson-learned-modal-" + projectMetricId);
    },
    resetMarkLessonLearnedForm() {
      this.markLessonLearnedForm = {
        name: "",
        description: "",
      };

      //Clear form
      this.showMarkLessonLearnedForm = false;
      this.$nextTick(() => {
        this.showMarkLessonLearnedForm = true;
      });
    },
    addProjectMetric() {
      this.$refs["add-project-metric-modal"].show();
    },
    editProjectMetric(id) {
      this.editingProjectMetric = this.projectMetrics.find(
        (a) => a.projectMetricId === id
      );
      //this.$forceUpdate();
      //this.editProjectMetricKey += 1;
      this.$nextTick(() => {
        this.$refs["edit-project-metric-modal"].show();
      });
      //this.$refs['edit-project-metric-modal'].show();
    },
    viewProjectMetric(id) {
      this.viewingProjectMetricId = id;

      this.$nextTick(() => {
        this.$refs["view-project-metric-modal"].show();
      });
    },
    closeProjectMetric(id) {
      this.closingProjectMetric = this.projectMetrics.find(
        (a) => a.projectMetricId === id
      );

      this.$nextTick(() => {
        this.$refs["close-project-metric-modal"].show();
      });
    },
    //Just update all of them
    refreshProjectMetric() {
      this.$emit("update");
    },
    addProjectMetricSubmit() {
      this.$emit("update");
      this.$refs["add-project-metric-modal"].cancel();
    },
    editProjectMetricSubmit() {
      this.$emit("update");
      this.$refs["edit-project-metric-modal"].cancel();
    },
    closeProjectMetricSubmit() {
      this.$emit("update");
      this.$refs["close-project-metric-modal"].cancel();
    },
    borderClassByRole(projectMetric) {
      let role =
        projectMetric.assignedToUsers.length > 0
          ? projectMetric.assignedToUsers[0].role
          : "";
      if (role === "Owner") {
        return "border-owner";
      } else if (role === "Contractor") {
        return "border-contractor";
      } else {
        return "";
      }
    },
    dataTemplateByType(type) {
      if (type === "Innovation" || type === "Efficiency") {
        return {
          overrides: [
            {
              actions: ["Add"],
              key: "status",
              value: "Closed",
            },
          ],
        };
      }
      return null;
    },
  },
  computed: {
    viewingProjectMetric() {
      return this.projectMetrics.find(
        (p) => p.projectMetricId === this.viewingProjectMetricId
      );
    },
    displayableProjectMetrics() {
      return this.showLimit > 0
        ? this.projectMetrics.slice(0, this.showLimit)
        : this.projectMetrics;
    },
    titleHeader() {
      var header = this.title;

      if (this.showRemainingCount) {
        let count = this.projectMetrics.length;
        header += `${count > 0 ? ` (${count} left)` : ""}`;
      }
      return header;
    },
    nextTwoYears() {
      var futureDates = [];

      let today = new Date();
      futureDates[0] = today.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
      for (var i = 1; i < 24; i++) {
        let date = new Date(new Date(today).setMonth(today.getMonth() + i));
        futureDates[i] = date.toLocaleString("default", {
          month: "long",
          year: "numeric",
        });
      }

      return futureDates;
    },
  },
};
</script>

<style lang="scss" scoped>
.border-owner {
  border-left-width: 6px;
  border-left-color: #4a708b;
}
.border-contractor {
  border-left-width: 6px;
  border-left-color: #34bf1b;
}
.metric {
  &__list {
  }
  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 16px;
  }
}
</style>